// keymap.ts - the shared VSCode-parity keymap for both apps' YAML/SQL
// editors. `defaultKeymap` already binds Mod-/ (toggleComment), Alt-Arrow
// (move line), Shift-Alt-Arrow (copy line), Mod-Alt-Arrow (add cursor),
// Shift-Mod-k (delete line), and Mod-[/Mod-] (indent) - this module does not
// re-list any of that. Folding and search bindings come directly from their
// upstream keymaps.
//
// Completion keys are owned exclusively by `dbtChartsCompletionExtensions` in
// completion.ts: upstream `completionKeymap` arrives with `autocompletion()`
// itself, plus the one explicit `Mod-Shift-Space` binding that module adds.
// They are not duplicated here.

import { defaultKeymap, historyKeymap, indentWithTab } from "@codemirror/commands";
import { foldKeymap } from "@codemirror/language";
import { searchKeymap } from "@codemirror/search";
import { EditorState, type Extension } from "@codemirror/state";
import { EditorView, drawSelection, keymap, type KeyBinding } from "@codemirror/view";
import { dbtChartsSearch } from "./search.js";

// `drawSelection` paints its own selection rectangles, and CM6's built-in
// colors for them are very pale (#d9d9d9 unfocused, #d7d4f0 focused), which is
// close to invisible on these editors' white background. State the color
// outright instead. `baseTheme` rather than `theme` keeps it a default an app
// can still override.
//
// The focused selector deliberately mirrors the full ancestor chain CM6's own
// focused rule uses. That rule carries five classes, so a shorter selector here
// loses on specificity and the pale default keeps winning in the one state that
// matters, the focused editor. Matching the shape ties on specificity, and this
// module's rules are appended later, so these win.
const selectionTheme = EditorView.baseTheme({
  ".cm-selectionBackground": { background: "#b9d3f8" },
  "&.cm-focused > .cm-scroller > .cm-selectionLayer .cm-selectionBackground": {
    background: "#9cc2f5",
  },
});

/**
 * The shared VSCode-parity keymap, plus the two extensions its multi-cursor
 * bindings are inert without. Each consuming app installs this once, in its
 * `EditorView` extensions.
 *
 * `allowMultipleSelections` is required because `defaultKeymap`'s add-cursor
 * commands and `searchKeymap`'s `selectNextOccurrence` both produce a multi-range
 * selection, which CM6 collapses back to one range unless multiple selections
 * are explicitly enabled.
 *
 * `drawSelection` is required to SEE them: the browser renders exactly one
 * native caret and one native selection, so extra cursors exist in the state
 * and take edits but paint nothing. `drawSelection` hides the native selection
 * and draws every range itself. Enabling one without the other gives a feature
 * that works invisibly, the same failure shape as the `Mod-/` `commentTokens`
 * gap this package's language.ts fixes.
 */
export function dbtChartsKeymap(): Extension {
  const bindings: KeyBinding[] = [
    indentWithTab,
    ...defaultKeymap,
    ...historyKeymap,
    ...foldKeymap,
    ...searchKeymap,
  ];
  return [
    EditorState.allowMultipleSelections.of(true),
    drawSelection(),
    selectionTheme,
    dbtChartsSearch(),
    keymap.of(bindings),
  ];
}
